import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from 'config';

// reducer
import { LOGIN, LOGOUT } from 'store/actions';
import accountReducer from 'store/accountReducer';

// ui
import Loader from 'ui-component/Loader';

const initialState = {
  isLoggedIn: false,
  isInitialized: false,
  user: null
};

const JWTContext = createContext(null);

// -------------------- helper functions --------------------

const isValidToken = (token) => {
  if (!token) return false;

  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return false;

    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(window.atob(base64));

    if (!payload?.exp) return false;

    return payload.exp * 1000 > Date.now();
  } catch (error) {
    console.error('Invalid token format:', error);
    return false;
  }
};

const getTokenExpiryTimeout = (token) => {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;

    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(window.atob(base64));

    if (!payload?.exp) return null;

    const expiresAt = payload.exp * 1000;
    return Math.max(expiresAt - Date.now(), 0);
  } catch (error) {
    return null;
  }
};

// ==============================|| JWT PROVIDER ||============================== //

export function JWTProvider({ children }) {
  const [state, dispatch] = useReducer(accountReducer, initialState);
  const logoutTimerRef = useRef(null);
  const interceptorRef = useRef(null);

  const setSession = (token) => {
    if (token) {
      localStorage.setItem('token', token);
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    } else {
      localStorage.removeItem('token');
      delete axios.defaults.headers.common.Authorization;
    }
  };

  const clearLogoutTimer = () => {
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
  };

  const handleSessionExpired = () => {
    clearLogoutTimer();
    setSession(null);

    dispatch({ type: LOGOUT });

    // redirect user to login page with session expired flag
    const currentPath = window.location.pathname;
    if (currentPath !== '/login') {
      window.location.href = '/login?session=expired';
    } else {
      window.location.href = '/login?session=expired';
    }
  };

  const startAutoLogoutTimer = (token) => {
    clearLogoutTimer();

    const timeout = getTokenExpiryTimeout(token);

    if (timeout === null) return;

    logoutTimerRef.current = setTimeout(() => {
      handleSessionExpired();
    }, timeout);
  };

  useEffect(() => {
    const token = localStorage.getItem('token');

    if (token && isValidToken(token)) {
      setSession(token);
      startAutoLogoutTimer(token);

      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          user: null
        }
      });
    } else {
      setSession(null);
      dispatch({ type: LOGOUT });
    }

    dispatch({ type: 'INITIALIZE' });

    return () => {
      clearLogoutTimer();
    };
  }, []);

  useEffect(() => {
    interceptorRef.current = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;

        if (status === 401) {
          handleSessionExpired();
        }

        return Promise.reject(error);
      }
    );

    return () => {
      if (interceptorRef.current !== null) {
        axios.interceptors.response.eject(interceptorRef.current);
      }
    };
  }, []);

  const login = async (email, password) => {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });

    if (!response.ok) {
      throw new Error('Invalid credentials');
    }

    const data = await response.json();

    if (!data?.token) {
      throw new Error('Token not received from server');
    }

    setSession(data.token);
    startAutoLogoutTimer(data.token);

    sessionStorage.setItem('showPhnxIntro', 'true');

    dispatch({
      type: LOGIN,
      payload: {
        isLoggedIn: true,
        user: { email }
      }
    });
  };

  const logout = () => {
    clearLogoutTimer();
    setSession(null);
    dispatch({ type: LOGOUT });
    window.location.href = '/login';
  };

  const register = async () => {};
  const resetPassword = async () => {};
  const updateProfile = async () => {};

  if (!state.isInitialized) {
    return <Loader />;
  }

  return (
    <JWTContext.Provider
      value={{
        ...state,
        login,
        logout,
        register,
        resetPassword,
        updateProfile
      }}
    >
      {children}
    </JWTContext.Provider>
  );
}

JWTProvider.propTypes = {
  children: PropTypes.node
};

export default JWTContext;
